/**
 * Question pack format (FR-8.1) — the uploaded/authored JSON document.
 *
 * This is the *authoring* shape a pack author uploads. It is validated (see
 * {@link validatePack}) and later snapshotted into a game's immutable
 * {@link PackSnapshot} at creation (FR-8.4). Forward-compatible with a future
 * generation feature (FR-8.5): unknown fields are ignored, not rejected.
 */

import type { Round, SpeechSegment } from './domain';

/** A single authored clue within a category. */
export interface PackClue {
  /** Unique across the whole pack. */
  id: string;
  /** The "answer" read aloud (shown to all). */
  prompt: string;
  /** The correct "question" (host-only). */
  response: string;
  /** Round-appropriate value. */
  value: number;
  /** Marks this clue as a Daily Double (hidden until uncovered). */
  isDailyDouble?: boolean;
  /** Optional per-clue timer override (seconds). */
  timerDuration?: number;
  /**
   * Optional read-aloud segmentation with dramatic pauses (#91, D7). Carried
   * through to the domain {@link Clue} at snapshot time; the dynamic generator
   * emits these for auto-host games. Absent for hand-authored packs (the client
   * derives pauses heuristically). Carries no answer.
   */
  speech?: SpeechSegment[];
  /**
   * Optional reference to pre-generated prompt audio for auto-host voice (#99,
   * NFR-6.9): a same-origin path to the cached MP3 of this clue's prompt read
   * aloud (e.g. `/audio/v1/<hash>.mp3`, served by CloudFront). Populated offline
   * (S3 of #99); absent for hand-authored packs and until the corpus is
   * audio-backfilled. Carried through to the domain {@link Clue} at snapshot time.
   * A pure optimization — carries no answer, and its absence falls back to
   * on-demand `/api/voice/tts` synthesis then the browser voice.
   */
  audioRef?: string;
}

/** A category column within one round's board. */
export interface PackCategory {
  name: string;
  clues: PackClue[];
}

/** One board round (JEOPARDY or DOUBLE_JEOPARDY). */
export interface PackRound {
  round: Round;
  categories: PackCategory[];
}

/** The single Final Jeopardy clue. */
export interface PackFinalJeopardy {
  categoryName: string;
  prompt: string;
  response: string;
}

/** The top-level authored pack document. */
export interface Pack {
  /** Author-facing pack name. */
  name: string;
  /** Pack format/content version string. */
  version: string;
  /** Board rounds. MUST include JEOPARDY and DOUBLE_JEOPARDY. */
  rounds: PackRound[];
  finalJeopardy: PackFinalJeopardy;
  /** Fallback timer duration (seconds) when a clue omits one. */
  defaultTimerDuration: number;
  /** Configurable max players (≤ 8, FR-2.5). */
  maxPlayers: number;
}

// ---------------------------------------------------------------------------
// Pack shape constraints (real-Jeopardy rules; tunable in one place)
// ---------------------------------------------------------------------------

/** Structural rules a valid pack MUST satisfy (FR-8.2). */
export const PACK_RULES = {
  /** Categories required per board round. */
  categoriesPerRound: 6,
  /** Clues required per category (i.e. rows on the board). */
  cluesPerCategory: 5,
  /**
   * Board rounds that MUST be present, and their allowed Daily Double count range
   * `[min, max]` (FR-5.1): DOUBLE_JEOPARDY MUST contain exactly two; JEOPARDY MAY
   * contain one, so 0 or 1 is accepted.
   */
  requiredRounds: [
    { round: 'JEOPARDY' as Round, minDailyDoubles: 0, maxDailyDoubles: 1 },
    { round: 'DOUBLE_JEOPARDY' as Round, minDailyDoubles: 2, maxDailyDoubles: 2 },
  ],
  minPlayers: 1,
  maxPlayersCeiling: 8,
} as const;

/**
 * Maximum accepted size (bytes) of an uploaded pack document (NFR-5.4). The
 * control-plane upload endpoint rejects any request body larger than this BEFORE
 * parsing it, so a hostile client can't exhaust memory with a giant blob or force
 * a huge `JSON.parse`. Lives in `shared` (beside the schema + validator) so the
 * server and any tooling agree on one limit — a single source of truth.
 *
 * 512 KiB comfortably fits a real pack: two 6×5 boards + a Final clue is a few KiB
 * of prompts/responses even with generous text, so this is ~100× headroom while
 * still bounding the request. There is no code execution path — the blob is only
 * ever `JSON.parse`d and run through {@link validatePack}.
 */
export const MAX_PACK_UPLOAD_BYTES = 512 * 1024;
